import React, { useState, useRef, useEffect } from "react";
import EpisodeInfo from "./EpisodeInfo";
import { useAudioPlayer } from "@/contexts/AudioPlayerContext";
import { Heart, ThumbsDown, ThumbsUp } from "lucide-react";

const NowPlaying = () => {
  const {
    currentEpisode,
    toggleFavorite,
    isFavorited,
    toggleDislike,
    isDisliked,
  } = useAudioPlayer();
  const [offset, setOffset] = useState(0);
  const touchStart = useRef(0);
  const isTouchDevice = useRef(false);

  const likeThreshold = 80;
  const likeActionThreshold = 120;
  const dislikeThreshold = -80;
  const dislikeActionThreshold = -120;

  useEffect(() => {
    isTouchDevice.current = "ontouchstart" in window;
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isTouchDevice.current) return;
    touchStart.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isTouchDevice.current) return;
    const currentTouch = e.touches[0].clientX;
    const diff = currentTouch - touchStart.current;
    const newOffset = Math.min(120, Math.max(-120, diff));
    setOffset(newOffset);
  };

  const handleTouchEnd = () => {
    if (!isTouchDevice.current) return;
    if (offset > likeActionThreshold) {
      toggleFavorite();
    } else if (offset < dislikeActionThreshold) {
      toggleDislike();
    }
    setOffset(0);
  };

  const handleMouseEnter = (side: "left" | "right") => {
    if (isTouchDevice.current) return;
    if (side === "left") {
      setOffset(likeThreshold);
    } else {
      setOffset(dislikeThreshold);
    }
  };

  const handleMouseLeave = () => {
    if (isTouchDevice.current) return;
    setOffset(0);
  };

  const getLikeButtonWidth = () => {
    if (offset < likeThreshold) return 80;
    if (offset > likeActionThreshold) return 400;
    const progress =
      (offset - likeThreshold) / (likeActionThreshold - likeThreshold);
    return Math.min(400, Math.max(80, 80 + progress * 320));
  };

  const getDislikeButtonWidth = () => {
    if (offset > dislikeThreshold) return 80;
    if (offset < dislikeActionThreshold) return 400;
    const progress =
      (offset - dislikeThreshold) / (dislikeActionThreshold - dislikeThreshold);
    return Math.min(400, Math.max(80, 80 + progress * 320));
  };

  if (!currentEpisode) return null;

  return (
    <div className="relative overflow-hidden touch-pan-y mb-4">
      {/* Hover zones */}
      {!isTouchDevice.current && (
        <>
          <div
            className="absolute inset-y-0 left-0 w-16 z-20"
            onMouseEnter={() => handleMouseEnter("left")}
            onMouseLeave={handleMouseLeave}
          />
          <div
            className="absolute inset-y-0 right-0 w-16 z-20"
            onMouseEnter={() => handleMouseEnter("right")}
            onMouseLeave={handleMouseLeave}
          />
        </>
      )}

      {/* Like button background */}
      <div
        className="absolute inset-y-0 left-0 flex items-center justify-start bg-green-500 text-white transition-all rounded-2xl"
        style={{
          width: getLikeButtonWidth(),
          opacity: Math.min(1, Math.abs(offset / likeThreshold)),
        }}
        onClick={toggleFavorite}
      >
        <div className="px-4">
          <Heart
            className={`h-6 w-6 ${isFavorited ? "fill-[#735fe2] text-[#735fe2]" : ""}`}
          />
        </div>
      </div>

      {/* Dislike button background */}
      <div
        className="absolute inset-y-0 right-0 flex items-center justify-end bg-red-500 text-white transition-all rounded-2xl"
        style={{
          width: getDislikeButtonWidth(),
          opacity: Math.min(1, Math.abs(offset / dislikeThreshold)),
        }}
      >
        <div className="px-4">
          <ThumbsDown
            className={`h-6 w-6 ${isDisliked ? "fill-[#ef4444] text-[#ef4444]" : ""}`}
          />
        </div>
      </div>

      {/* Main content */}
      <div
        className="relative z-10 w-full p-4 bg-white/5 backdrop-blur-md rounded-xl border border-white/20 transition-transform"
        style={{ transform: `translateX(${offset}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <EpisodeInfo
          title={currentEpisode.title}
          imageUrl={currentEpisode.imageUrl}
          listens={currentEpisode.listens}
          likes={currentEpisode.likes}
        />
      </div>
    </div>
  );
};

export default NowPlaying;
